import React, {
  createElement,
  HTMLAttributes,
  ReactNode,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';

type Props = {
  children: ReactNode;
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  /** Extra delay in ms after the element enters view (AOS-style). */
  delay?: number;
  /** When true, reveal immediately on mount (hero / above-the-fold). */
  immediate?: boolean;
  /** Controlled open state when `as="details"`. */
  open?: boolean;
} & Omit<HTMLAttributes<HTMLElement>, 'children' | 'className' | 'open'>;

/**
 * Scroll-triggered fade-up reveal, matching QuestionPro marketing pages
 * that use AOS (`data-aos="fade-up"`): opacity 0 → 1 and translateY(100px) → 0.
 */
function Reveal({
  children,
  as = 'div',
  className = '',
  delay = 0,
  immediate = false,
  style,
  ...rest
}: Props) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (immediate) {
      // Next frame so the initial hidden styles paint before transitioning.
      const id = window.requestAnimationFrame(() => setVisible(true));
      return () => window.cancelAnimationFrame(id);
    }

    const node = ref.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.12,
      },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [immediate]);

  const classes = [
    'brand-landing-reveal',
    visible ? 'is-visible' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return createElement(
    as,
    {
      ref,
      className: classes,
      style: {
        ...style,
        ...(visible && delay ? { transitionDelay: `${delay}ms` } : null),
      },
      ...rest,
    },
    children,
  );
}

export default memo(Reveal);
